import { formatHumanName } from "@medplum/core";
import type { Device, Location, Practitioner, Schedule } from "@medplum/fhirtypes";
import { getTRPCClient, SCHEDULING_PARAMS_URL } from "@repo/api";
import {
  type CreateScheduleInput,
  type DeleteScheduleInput,
  deleteScheduleInputSchema,
  scheduleIdSchema,
  type UpdateScheduleInput,
  updateScheduleInputSchema,
} from "@repo/api/schemas";
import type { ScheduleId } from "@repo/domain";
import { SENTINEL_SERVICE_TYPE_SYSTEM } from "@repo/fhir-systems/code-systems";
import type { Collection } from "@tanstack/db";
import { and, eq, useLiveQuery } from "@tanstack/react-db";
import { defineWrite } from "../actions/define-write";
import { useCollection } from "../core/hooks";
import { getDataStoreClient } from "../core/provider";
import type { ScheduleRow, SlotRow } from "../fhir/types";
import { applyScheduleFields, optimisticSchedule } from "./_schedule-optimistic";
import { optimisticAvailabilityBlock } from "./availability-block";
import type { Period } from "./shared/types";

const SCHEDULE_COLLECTION = "schedule";
const PRACTITIONER_COLLECTION = "practitioner";
const LOCATION_COLLECTION = "location";
const DEVICE_COLLECTION = "device";

const SENTINEL_SERVICE_TYPE_CODE = "generic";

// ── DTO ─────────────────────────────────────────────────────────

/** Stable, small coded-value shape. FHIR CodeableConcept is collapsed to the first Coding. */
interface CodedValue {
  system: string;
  code: string;
  display?: string;
}

interface SchedulingDuration {
  value: number;
  unit: "min" | "h";
}

interface SchedulingParameters {
  serviceType?: CodedValue;
  duration: SchedulingDuration;
  timezone?: string;
  availability?: {
    dayOfWeek: Array<"mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun">;
    startTime: string;
    endTime: string;
  };
  bufferBefore?: SchedulingDuration;
  bufferAfter?: SchedulingDuration;
  alignmentInterval?: SchedulingDuration;
  alignmentOffset?: SchedulingDuration;
  bookingAdvanceLimit?: { value: number; unit: "d" | "wk" };
  bookingLimit?: Array<{ maxPerPeriod: number; periodUnit: "d" | "wk" }>;
}

/**
 * Schedule — normalized read-model for calendar/booking UI.
 * Actor is flattened (`actor.type` + `actor.id` + resolved display name).
 */
export interface ScheduleDto {
  scheduleId: ScheduleId;
  active: boolean;

  actor: {
    type: "Practitioner" | "Location" | "Device" | "Other";
    id: string;
    /** Display name resolved from the actor's resource (Practitioner.name, Location.name, Device.deviceName). */
    display?: string;
  };

  planningHorizon?: Period;

  serviceCategory?: CodedValue[];
  serviceType?: CodedValue[];
  specialty?: CodedValue[];

  comment?: string;

  schedulingParameters: SchedulingParameters[];
}

type ScheduleActorType = ScheduleDto["actor"]["type"];

// ── Projection ──────────────────────────────────────────────────

function fromCodeable(value: {
  coding?: Array<{ system?: string; code?: string; display?: string }>;
}): CodedValue | undefined {
  const c = value.coding?.[0];
  if (!c?.system || !c.code) return undefined;
  return { system: c.system, code: c.code, ...(c.display ? { display: c.display } : {}) };
}

function fromCodeableArray(
  values: Array<{ coding?: Array<{ system?: string; code?: string; display?: string }> }> | undefined,
): CodedValue[] | undefined {
  if (!values) return undefined;
  const out = values.map(fromCodeable).filter((v): v is CodedValue => !!v);
  return out.length ? out : undefined;
}

function parseDuration(
  exts: Array<{ url?: string; valueDuration?: { value?: number; code?: string } }>,
  url: string,
): SchedulingDuration | undefined {
  const ext = exts.find((e) => e.url === url);
  if (!ext?.valueDuration?.value || !ext.valueDuration.code) return undefined;
  return { value: ext.valueDuration.value, unit: ext.valueDuration.code as SchedulingDuration["unit"] };
}

function parseAvailability(
  nested: Array<{
    url?: string;
    extension?: Array<{ url?: string; extension?: Array<{ url?: string; valueCode?: string; valueTime?: string }> }>;
  }>,
): SchedulingParameters["availability"] {
  const availabilityExt = nested.find((e) => e.url === "availability");
  const availableTime = availabilityExt?.extension?.find((e) => e.url === "availableTime");
  const subs = availableTime?.extension ?? [];
  const dayOfWeek = subs
    .filter((e) => e.url === "daysOfWeek")
    .map((e) => e.valueCode)
    .filter((v): v is string => !!v);
  const startRaw = subs.find((e) => e.url === "availableStartTime")?.valueTime;
  const endRaw = subs.find((e) => e.url === "availableEndTime")?.valueTime;
  if (!dayOfWeek.length || !startRaw || !endRaw) return undefined;
  return {
    dayOfWeek: dayOfWeek as NonNullable<SchedulingParameters["availability"]>["dayOfWeek"],
    startTime: startRaw.slice(0, 5),
    endTime: endRaw.slice(0, 5),
  };
}

function parseBookingLimits(
  nested: Array<{ url?: string; extension?: Array<{ url?: string; valueInteger?: number; valueCode?: string }> }>,
): Array<{ maxPerPeriod: number; periodUnit: "d" | "wk" }> {
  return nested
    .filter((e) => e.url === "bookingLimit")
    .map((e) => {
      const max = e.extension?.find((x) => x.url === "maxPerPeriod")?.valueInteger;
      const unit = e.extension?.find((x) => x.url === "periodUnit")?.valueCode as "d" | "wk" | undefined;
      if (max === undefined || !unit) return null;
      return { maxPerPeriod: max, periodUnit: unit };
    })
    .filter((v): v is { maxPerPeriod: number; periodUnit: "d" | "wk" } => !!v);
}

function parseSchedulingParamsExtension(extensions: Schedule["extension"]): SchedulingParameters[] {
  const out: SchedulingParameters[] = [];
  for (const ext of extensions ?? []) {
    if (ext.url !== SCHEDULING_PARAMS_URL) continue;
    const nested = (ext.extension ?? []) as Array<{
      url?: string;
      valueCode?: string;
      valueCodeableConcept?: { coding?: Array<{ system?: string; code?: string; display?: string }> };
      valueDuration?: { value?: number; code?: string };
      extension?: Array<{ url?: string; valueInteger?: number; valueCode?: string; valueTime?: string }>;
    }>;

    const duration = parseDuration(nested, "duration");
    if (!duration) continue;

    const availability = parseAvailability(nested);
    const bookingLimit = parseBookingLimits(nested);

    const rawServiceType = nested.find((e) => e.url === "serviceType")?.valueCodeableConcept;
    const st = rawServiceType ? fromCodeable(rawServiceType) : undefined;
    const isSentinel = st?.system === SENTINEL_SERVICE_TYPE_SYSTEM && st.code === SENTINEL_SERVICE_TYPE_CODE;
    const serviceType = isSentinel ? undefined : st;

    const timezone = nested.find((e) => e.url === "timezone")?.valueCode;
    const bufferBefore = parseDuration(nested, "bufferBefore");
    const bufferAfter = parseDuration(nested, "bufferAfter");
    const alignmentInterval = parseDuration(nested, "alignmentInterval");
    const alignmentOffset = parseDuration(nested, "alignmentOffset");
    const bookingAdv = parseDuration(nested, "bookingAdvanceLimit");

    out.push({
      duration,
      ...(serviceType ? { serviceType } : {}),
      ...(timezone ? { timezone } : {}),
      ...(availability ? { availability } : {}),
      ...(bufferBefore ? { bufferBefore } : {}),
      ...(bufferAfter ? { bufferAfter } : {}),
      ...(alignmentInterval ? { alignmentInterval } : {}),
      ...(alignmentOffset ? { alignmentOffset } : {}),
      ...(bookingAdv ? { bookingAdvanceLimit: { value: bookingAdv.value, unit: bookingAdv.unit as "d" | "wk" } } : {}),
      ...(bookingLimit.length ? { bookingLimit } : {}),
    });
  }
  return out;
}

function resolveActorDisplay(
  actorType: ScheduleActorType,
  practitioner: Practitioner | undefined,
  location: Location | undefined,
  device: Device | undefined,
  inlineDisplay: string | undefined,
): string | undefined {
  if (inlineDisplay) return inlineDisplay;
  if (actorType === "Practitioner" && practitioner?.name?.[0]) return formatHumanName(practitioner.name[0]);
  if (actorType === "Location") return location?.name;
  if (actorType === "Device" && device?.deviceName?.length) {
    const friendly = device.deviceName.find((n) => n.type === "user-friendly-name");
    return friendly?.name ?? device.deviceName[0]?.name;
  }
  return undefined;
}

interface ScheduleRowInput {
  schedule: ScheduleRow;
  practitioner?: Practitioner;
  location?: Location;
  device?: Device;
}

export function projectScheduleRow({ schedule, practitioner, location, device }: ScheduleRowInput): ScheduleDto {
  const inlineDisplay = schedule.actor?.[0]?.display;
  const actor: ScheduleDto["actor"] = {
    type: schedule.actorType,
    id: schedule.actorId ?? "",
    ...(() => {
      const display = resolveActorDisplay(schedule.actorType, practitioner, location, device, inlineDisplay);
      return display ? { display } : {};
    })(),
  };

  return {
    scheduleId: scheduleIdSchema.parse(schedule.domainId),
    active: schedule.active ?? true,
    actor,
    ...(schedule.planningHorizon
      ? {
          planningHorizon: {
            ...(schedule.planningHorizon.start ? { start: schedule.planningHorizon.start } : {}),
            ...(schedule.planningHorizon.end ? { end: schedule.planningHorizon.end } : {}),
          },
        }
      : {}),
    ...(fromCodeableArray(schedule.serviceCategory)
      ? { serviceCategory: fromCodeableArray(schedule.serviceCategory) }
      : {}),
    ...(fromCodeableArray(schedule.serviceType) ? { serviceType: fromCodeableArray(schedule.serviceType) } : {}),
    ...(fromCodeableArray(schedule.specialty) ? { specialty: fromCodeableArray(schedule.specialty) } : {}),
    ...(schedule.comment !== undefined ? { comment: schedule.comment } : {}),
    schedulingParameters: parseSchedulingParamsExtension(schedule.extension),
  };
}

/**
 * Post-projection guard that strips any ScheduleDto whose actor.type or
 * actor.id doesn't match the requested actorRef.
 *
 * TanStack DB's IVM can transiently yield stale rows during a collection
 * sync or when actorRef switches (e.g. Device→Location). The WHERE clause
 * inside useLiveQuery is evaluated against the collection snapshot that
 * existed when the query was compiled; rows added/removed mid-sync slip
 * through until the next IVM cycle settles. Applying this guard atomically
 * on the projected result prevents mismatched DTOs from reaching callers.
 *
 * When actorRef is undefined (list-all mode) the function is a no-op.
 */
export function filterSchedulesByActorRef(actorRef: string | undefined, dtos: ScheduleDto[]): ScheduleDto[] {
  if (!actorRef) return dtos;
  const [type, id] = actorRef.split("/");
  return dtos.filter((dto) => dto.actor.type === type && dto.actor.id === id);
}

// ── Hooks ───────────────────────────────────────────────────────

interface UseSchedulesResult {
  schedules: ScheduleDto[];
  isLoading: boolean;
  isReady: boolean;
  isError: boolean;
}

/**
 * List schedules, joined with actor resource for display name resolution.
 * When `actorRef` is provided (e.g. `"Practitioner/abc"`), filters to that actor's schedules.
 *
 * Actor is polymorphic (Practitioner | Location | Device). Denormalized FK
 * columns (`practitionerId`/`locationId`/`deviceId`) on the Schedule row
 * let us do three independent left joins; at most one resolves per schedule
 * and `projectScheduleRow` picks the display from whichever matched.
 */
export function useSchedules(actorRef?: string): UseSchedulesResult {
  const scheduleCollection = useCollection(SCHEDULE_COLLECTION);
  const practitionerCollection = useCollection(PRACTITIONER_COLLECTION);
  const locationCollection = useCollection(LOCATION_COLLECTION);
  const deviceCollection = useCollection(DEVICE_COLLECTION);

  const [actorType, actorId] = actorRef?.split("/") ?? [];

  const result = useLiveQuery({
    queryKey: [
      scheduleCollection.id,
      practitionerCollection.id,
      locationCollection.id,
      deviceCollection.id,
      "schedules",
      actorType ?? null,
      actorId ?? null,
    ],
    query: (q) => {
      const base = q
        .from({ schedule: scheduleCollection })
        .join(
          { practitioner: practitionerCollection },
          ({ schedule, practitioner }) => eq(practitioner.domainId, schedule.practitionerId),
          "left",
        )
        .join(
          { location: locationCollection },
          ({ schedule, location }) => eq(location.domainId, schedule.locationId),
          "left",
        )
        .join({ device: deviceCollection }, ({ schedule, device }) => eq(device.domainId, schedule.deviceId), "left");

      const filtered = actorType
        ? base.where(({ schedule }) => and(eq(schedule.actorType, actorType), eq(schedule.actorId, actorId)))
        : base;

      return filtered.fn.select((row) =>
        projectScheduleRow({
          schedule: row.schedule,
          practitioner: row.practitioner as Practitioner | undefined,
          location: row.location as Location | undefined,
          device: row.device as Device | undefined,
        }),
      );
    },
  });

  const raw = (result.data as ScheduleDto[] | undefined) ?? [];
  // Closing a schedule is a soft delete — the Schedule stays, marked inactive,
  // so the Appointments and Slots already on it keep resolving. The list has to
  // drop those itself or a closed calendar reappears the moment the
  // subscription echoes it. `useSchedule` keeps them: a direct link to a closed
  // calendar should still render its history.
  const schedules = filterSchedulesByActorRef(actorRef, raw).filter((s) => s.active);

  return {
    schedules,
    isLoading: result.isLoading,
    isReady: result.isReady,
    isError: result.isError,
  };
}

interface UseScheduleResult {
  schedule: ScheduleDto | null;
  isLoading: boolean;
  isReady: boolean;
}

/** Single schedule by id. `schedule` is `null` until ready or if not found. */
export function useSchedule(scheduleId: string | undefined): UseScheduleResult {
  const scheduleCollection = useCollection(SCHEDULE_COLLECTION);
  const practitionerCollection = useCollection(PRACTITIONER_COLLECTION);
  const locationCollection = useCollection(LOCATION_COLLECTION);
  const deviceCollection = useCollection(DEVICE_COLLECTION);

  const result = useLiveQuery({
    queryKey: [
      scheduleCollection.id,
      practitionerCollection.id,
      locationCollection.id,
      deviceCollection.id,
      "schedule",
      scheduleId ?? null,
    ],
    query: (q) => {
      if (!scheduleId) return undefined;
      return q
        .from({ schedule: scheduleCollection })
        .where(({ schedule }) => eq(schedule.domainId, scheduleId))
        .join(
          { practitioner: practitionerCollection },
          ({ schedule, practitioner }) => eq(practitioner.domainId, schedule.practitionerId),
          "left",
        )
        .join(
          { location: locationCollection },
          ({ schedule, location }) => eq(location.domainId, schedule.locationId),
          "left",
        )
        .join({ device: deviceCollection }, ({ schedule, device }) => eq(device.domainId, schedule.deviceId), "left")
        .fn.select((row) =>
          projectScheduleRow({
            schedule: row.schedule,
            practitioner: row.practitioner as Practitioner | undefined,
            location: row.location as Location | undefined,
            device: row.device as Device | undefined,
          }),
        )
        .findOne();
    },
  });

  return {
    schedule: (result.data as ScheduleDto | undefined) ?? null,
    isLoading: result.isLoading,
    isReady: result.isReady,
  };
}

// ── Mutations ───────────────────────────────────────────────────

const schedules = () => getDataStoreClient().getCollection(SCHEDULE_COLLECTION);

/** Rows are keyed by domain identity; this only asserts the row is loaded. */
function resourceIdForDomainId(domainId: ScheduleId): string {
  if (!schedules().has(domainId)) throw new Error(`Schedule '${domainId}' is not loaded`);
  return domainId;
}

type OpenScheduleInput = CreateScheduleInput & { actorDisplay?: string };

export function createOpenScheduleAction(deps: {
  persist: Parameters<typeof defineWrite<CreateScheduleInput>>[0]["persist"];
  collections: () => readonly Collection<ScheduleRow, string>[];
  slots: () => Collection<SlotRow, string>;
}) {
  const write = defineWrite<OpenScheduleInput>({
    optimistic: (input) => {
      deps.collections()[0]?.insert(optimisticSchedule(input));
      for (const [index, block] of (input.initialBlocks ?? []).entries()) {
        deps
          .slots()
          .insert(
            optimisticAvailabilityBlock(
              { ...block, scheduleId: scheduleIdSchema.parse(input.id) },
              `block-${index + 1}`,
            ),
          );
      }
    },
    persist: ({ actorDisplay: _display, ...input }, options) => deps.persist(input, options),
  });
  return async (input: OpenScheduleInput): Promise<void> => {
    await write(input).isPersisted.promise;
  };
}

export const openSchedule = createOpenScheduleAction({
  persist: async (input, options) => {
    await getTRPCClient().schedule.openSchedule.mutate(input, options);
  },
  collections: () => [schedules()],
  slots: () => getDataStoreClient().getCollection("slot"),
});

export function createAdjustScheduleAction(deps: {
  persist: Parameters<typeof defineWrite<UpdateScheduleInput>>[0]["persist"];
  collections: () => readonly Collection<ScheduleRow, string>[];
}) {
  const write = defineWrite<UpdateScheduleInput>({
    optimistic: (params) => {
      const input = updateScheduleInputSchema.parse(params);
      deps.collections()[0]?.update(input.id, (draft) => {
        applyScheduleFields(draft, input);
      });
    },
    persist: deps.persist,
  });
  return async (input: UpdateScheduleInput): Promise<void> => {
    await write(input).isPersisted.promise;
  };
}

export const adjustSchedule = createAdjustScheduleAction({
  persist: async (input, options) => {
    await getTRPCClient().schedule.adjustSchedule.mutate(input, options);
  },
  collections: () => [schedules()],
});

/** Intent: "close a schedule" */
export const closeSchedule = defineWrite<DeleteScheduleInput>({
  optimistic: (params) => {
    const input = deleteScheduleInputSchema.parse(params);
    // Marks it inactive rather than dropping the row: the server does the same,
    // so a delete here would be undone the moment the subscription echoed the
    // Schedule back. `useSchedules` hides inactive ones, so it still goes.
    schedules().update(resourceIdForDomainId(input.id), (draft) => {
      draft.active = false;
    });
  },
  persist: async (params, options) => {
    await getTRPCClient().schedule.closeSchedule.mutate(params, options);
  },
});
